/**
 * Auth context — the single source of truth for "who is logged in".
 *
 * Auth is cookie-only (wr_access/wr_refresh/wr_csrf). There is no token
 * in JS-accessible storage. On mount we call GET /api/v1/me; if the
 * access cookie is stale the api client transparently refreshes once
 * (the boot probe included — it just never navigates on failure).
 *
 * Shapes are sourced from what /me actually returns (MeResponse) — see
 * backend/internal/handlers/auth.go meResponse.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { recordSession } from "./session-ready";
import { isPasskeyCancelled, signInWithPasskey, type PasskeyCancelled } from "./passkey";
import { authApi } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { setStoredEntityId } from "@/hooks/use-entities";
import { isPoshOnly } from "@/lib/nav";
import { clearPoshQuizStorage } from "@/lib/posh-quiz-storage";
import type {
  LoginRequest,
  LoginResponse,
  LoginResult,
  MeResponse,
  MeTenant,
} from "@/types/auth";

// localStorage key used by query-client.ts's persister. Mirrored here
// so logout() can clear it independent of the import order (the
// persister is also keyed by this name).
const PERSISTED_QUERY_CACHE_KEY = "wr-query-cache";

// Drop BOTH the in-memory react-query cache and the persisted localStorage
// copy, plus the stored ACTIVE-ENTITY id. Called at EVERY auth boundary —
// login, activate (tenant switch), logout AND register — so no user's cached
// queries (timesheet, /me/pay payslips, leave, expenses, approvals…) can ever
// rehydrate into a different identity on a shared/kiosk machine. The
// persisted-cache rehydration at app boot is the reason clearing on logout
// alone is insufficient: login() must clear too, or a fresh sign-in after a
// prior session reuses the old cache. (docs/49 P1-2.)
//
// The entity id is the same class of cross-identity state (2026-07-05 bug):
// wr_active_entity_id is tenant-agnostic sessionStorage, and the interceptor
// injects it as X-Entity-Id on EVERY request — carried across a tenant
// switch/registration it makes the fail-closed EntityScope middleware 403
// every call ("entity not found in this tenant"), bricking the new session
// at the DPDP consent modal. Clearing at the boundary lets useActiveEntity
// re-resolve to the new tenant's primary entity.
//
// Exported (as clearClientAuthState) because the register page establishes
// its session via authApi.register + refresh() WITHOUT passing through
// login()/activate() — it must clear this state itself.
export function clearClientAuthState(): void {
  queryClient.clear();
  try {
    window.localStorage.removeItem(PERSISTED_QUERY_CACHE_KEY);
  } catch {
    /* localStorage disabled / quota — non-fatal */
  }
  setStoredEntityId(null);
}

// Internal alias — existing call sites below read at the old name.
const clearCachedQueries = clearClientAuthState;

interface AuthContextValue {
  /** The /me payload, or null when unauthenticated. */
  user: MeResponse | null;
  /** True until the initial /me probe resolves. */
  loading: boolean;
  /**
   * True when there IS an authenticated identity but NO active tenant
   * membership yet — the identity-only session that exists between a
   * >=2-membership login and the /auth/activate that picks a workspace.
   * The dashboard guard uses this to force the picker before any
   * tenant UI renders. (false while loading / signed out / tenant-ready.)
   */
  identityOnly: boolean;
  /**
   * Authenticate (email + password — ID.2 identity auth, no slug).
   * Returns the discriminated result so the caller can branch on:
   *  - `select_workspace` → the identity has >=2 memberships; an
   *    identity-only session cookie was set but there is NO active
   *    tenant. We hydrate /me (so `identityOnly` flips true) and the
   *    caller opens the picker → /auth/activate.
   *  - `totp_required` on a loginResponse → route to /2fa-verify.
   *  - otherwise → a full tenant session; /me is hydrated.
   */
  login: (body: LoginRequest) => Promise<LoginResult>;
  /**
   * Passwordless sign-in with a passkey (optionally from the email
   * field's autofill). Resolves to the same union as `login` — the caller
   * routes it identically — or PASSKEY_CANCELLED when the user dismissed
   * the browser sheet. The session crosses the same auth boundary as
   * `login` (cached queries cleared, /me hydrated).
   */
  loginWithPasskey: (opts?: {
    autofill?: boolean;
    shouldContinue?: () => boolean;
  }) => Promise<LoginResult | PasskeyCancelled>;
  /**
   * Establish/switch the active tenant by slug (POST /auth/activate).
   * THE single path for both the post-login picker AND the in-app
   * instant switch — no password, no logout. On success it re-hydrates
   * /me so the whole app re-renders in the new tenant. Returns the
   * loginResponse so the caller can honour totp_required.
   */
  activate: (slug: string) => Promise<LoginResponse>;
  /** Clear the server session + local state. */
  logout: () => Promise<void>;
  /** Re-fetch /me (e.g. after enabling TOTP or switching company). */
  refresh: () => Promise<MeResponse | null>;
  /**
   * Merge fields into the in-memory /me payload without a round-trip —
   * an optimistic update the caller reverts on failure (the POSH training
   * language switch, lib/posh-lang.ts). No-op while signed out.
   */
  patchUser: (patch: Partial<MeResponse>) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async (): Promise<MeResponse | null> => {
    try {
      const me = await authApi.getMe();
      // Recorded straight away (not only in the effect below) so a
      // navigation right after login/activate sees the new session.
      recordSession(me);
      setUser(me);
      return me;
    } catch {
      // A 401 here just means "no usable session" (e.g. called after a
      // logout race). The api client already attempted its single
      // refresh-then-retry before this rejected, so treat it as
      // signed-out rather than re-driving auth.
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        // probe:true sets _suppressAuthRedirect on the request config.
        // On a 401 the response interceptor first tries ONE refresh — the
        // shared, coalesced refresh every request uses, serialised across
        // tabs (lib/api/refresh-coordination.ts) — and replays /me once,
        // so reloading a page after the 15-minute access cookie expired
        // keeps the session the refresh cookie still holds instead of
        // landing on /login (2026-10 re-test, payroll #6 / books #7). It
        // never hard-redirects: when the refresh fails too (an anonymous
        // visitor on /register or /forgot has no refresh cookie) it just
        // rejects, and the user quietly resolves to null.
        const me = await authApi.getMe({ probe: true });
        if (active) setUser(me);
      } catch {
        if (active) setUser(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(
    async (body: LoginRequest): Promise<LoginResult> => {
      const res = await authApi.login(body);
      // P1-2: clear any prior identity's cached queries (in-memory + the
      // persisted localStorage copy that rehydrates at boot) BEFORE we
      // hydrate /me for the newly authenticated user. Without this, a fresh
      // sign-in on a shared machine could briefly render the previous user's
      // cached data (e.g. their timesheet or payslips).
      clearCachedQueries();
      // select_workspace: the identity has >=2 memberships. The backend
      // DID set an identity-only session cookie (no active tenant), so
      // we DO hydrate /me — it succeeds and `identity_id` is set with
      // no `tenant_id`, flipping `identityOnly` true. The caller then
      // opens the picker; /auth/activate completes the pick. (Hydrating
      // here means a refresh on the picker route keeps the user in the
      // identity-only state instead of looking signed-out.)
      await refresh();
      return res;
    },
    [refresh],
  );

  const loginWithPasskey = useCallback(
    async (opts?: {
      autofill?: boolean;
      shouldContinue?: () => boolean;
    }): Promise<LoginResult | PasskeyCancelled> => {
      const res = await signInWithPasskey(opts);
      if (isPasskeyCancelled(res)) return res;
      // Same auth boundary as login(): no earlier identity's cache survives.
      clearCachedQueries();
      await refresh();
      return res;
    },
    [refresh],
  );

  const activate = useCallback(
    async (slug: string): Promise<LoginResponse> => {
      // POST /auth/activate {slug} → full (rotated) tenant cookies.
      // Re-hydrate /me so the entire app (sidebar features, tenant
      // name, role-gated UI) re-renders in the newly active company —
      // no logout, no navigation to a re-auth screen.
      const res = await authApi.activate({ slug });
      // 2026-06-04 cross-tenant cache fix: drop every cached query (now
      // incl. the persisted copy) BEFORE refresh()/setUser propagate, so
      // children that re-render under the new tenant slug observe `idle`
      // state and trigger fresh fetches — never tenant-A rows under a
      // tenant-B URL. Same helper as login/logout so all auth boundaries
      // behave identically.
      clearCachedQueries();
      await refresh();
      return res;
    },
    [refresh],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
      // Drop the in-memory + persisted cache so nothing tenant/identity-
      // scoped survives the boundary or rehydrates on the next boot.
      // The legacy `WR_PURGE_CACHES` postMessage targeted a service-
      // worker handler that doesn't exist in this codebase (dropped
      // during the Next.js → Vite migration); removed to avoid false
      // confidence.
      clearCachedQueries();
      // POSH quiz attempts in progress (sessionStorage `wr_posh_quiz_v1:*`)
      // go too: the next person on a shared device must not see them. The
      // key also carries the user id, so a re-login of the SAME person
      // after a session expiry (login(), not logout) keeps their answers.
      // Never throws.
      clearPoshQuizStorage();
    }
  }, []);

  // Route loaders read this before prefetching tenant data (session-ready.ts).
  useEffect(() => {
    recordSession(user);
  }, [user]);

  // Identity-only = authenticated human with NO resolved membership —
  // the >=2-membership login state before /auth/activate. The robust
  // discriminant is user_id===0: an identity-only /me has user_id 0 +
  // role "" + no tenant_id. A PLATFORM identity (super_admin) also has
  // no tenant_id but DOES have a real user_id + role, so keying on
  // user_id (not tenant_id) keeps platform users out of the picker.
  const identityOnly = user !== null && user.user_id === 0;

  const patchUser = useCallback((patch: Partial<MeResponse>) => {
    setUser((u) => (u ? { ...u, ...patch } : u));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      identityOnly,
      login,
      loginWithPasskey,
      activate,
      logout,
      refresh,
      patchUser,
    }),
    [user, loading, identityOnly, login, loginWithPasskey, activate, logout, refresh, patchUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
}

/**
 * Tenant plan feature flags for the current user (from /me.features).
 * Use this to gate module nav/UI. Returns an array plus a `has` helper.
 */
export function useFeatures(): {
  features: string[];
  has: (feature: string) => boolean;
} {
  const { user } = useAuth();
  return useMemo(() => {
    const features = user?.features ?? [];
    const set = new Set(features);
    return { features, has: (f: string) => set.has(f) };
  }, [user]);
}

/**
 * True for a member of a POSH only company (no `hr_platform` in
 * /me.features — migration 162). False while /me loads and for sessions
 * without an active company, so nothing flashes the POSH only view.
 */
export function usePoshOnly(): boolean {
  const { user } = useAuth();
  return !!user?.tenant_id && isPoshOnly(user.features);
}

/** The current user's tenant block (from /me.tenant), or null. */
export function useTenant(): MeTenant | null {
  const { user } = useAuth();
  return user?.tenant ?? null;
}
